import React, { useMemo } from "react";
import type { FormikErrors, FormikTouched } from "formik";
import { WorkingHoursPickerField } from "./WorkingHoursPickerField";
import { useTranslation } from "react-i18next";
import { Col, Form, Row } from "react-bootstrap";
import { getWorkingHoursFieldMeta } from "./helpers/getWorkingHoursFieldMeta";
import { TooltipCustom } from "@services/ui-components";

interface WorkSchedulesSectionFieldNames {
    general: string;
    kitchen: string;
}

type WorkSchedulesSectionProps<FormValues extends object> = {
    title: string;
    timeZone: string;
    values: FormValues;
    errors: FormikErrors<FormValues>;
    touched: FormikTouched<FormValues>;
    setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void;
    fieldNames: WorkSchedulesSectionFieldNames;
    disabled?: boolean;
};

const labels = {
    generalLabel: "Working hours of the establishment",
    kitchenLabel: "Kitchen working hours",
    generalModalTitle: "Working hours of the establishment",
    kitchenModalTitle: "Kitchen working hours",
};

const WorkSchedulesSection = <FormValues extends object>(props: WorkSchedulesSectionProps<FormValues>) => {
    const { t } = useTranslation();

    const { title, values, timeZone, errors, touched, setFieldValue, fieldNames, disabled = false } = props;

    const general = useMemo(
        () =>
            getWorkingHoursFieldMeta({
                fieldName: fieldNames.general,
                values,
                errors,
                touched,
            }),
        [fieldNames.general, values, errors, touched]
    );

    const kitchen = useMemo(
        () =>
            getWorkingHoursFieldMeta({
                fieldName: fieldNames.kitchen,
                values,
                errors,
                touched,
            }),
        [fieldNames.kitchen, values, errors, touched]
    );

    return (
        <Row className="mb-5">
            <Col className="d-flex gap-7 flex-column">
                <Row>
                    <Col className="d-flex gap-2">
                        <h5 className="mb-0 fs-24">{t(title)}</h5>
                        <TooltipCustom placement="top" text={t("The work schedule will be displayed in the qr-menu")}>
                            <i className="bi bi-info-circle fs-16"></i>
                        </TooltipCustom>
                    </Col>
                </Row>
                <Row>
                    <Col sm={3} xxl={2} style={{ minWidth: 0 }}>
                        <Form.Label className="fs-7 fw-bold mb-0 text-break">{t(labels.generalLabel)}</Form.Label>
                    </Col>
                    <Col sm={9} xxl={10}>
                        <WorkingHoursPickerField
                            modalTitle={t(labels.generalModalTitle)}
                            timeZone={timeZone}
                            value={general.value}
                            onChange={(newValue) => {
                                setFieldValue(fieldNames.general, newValue, true);
                            }}
                            disabled={disabled}
                            isInvalid={general.isInvalid}
                            errorText={general.errorText}
                        />
                    </Col>
                </Row>
                <Row>
                    <Col sm={3} xxl={2}>
                        <Form.Label className="fs-7 fw-bold mb-0">{t(labels.kitchenLabel)}</Form.Label>
                    </Col>
                    <Col sm={9} xxl={10}>
                        <WorkingHoursPickerField
                            modalTitle={t(labels.kitchenModalTitle)}
                            timeZone={timeZone}
                            value={kitchen.value}
                            onChange={(newValue) => {
                                setFieldValue(fieldNames.kitchen, newValue, true);
                            }}
                            disabled={disabled}
                            isInvalid={kitchen.isInvalid}
                            errorText={kitchen.errorText}
                        />
                    </Col>
                </Row>
            </Col>
        </Row>
    );
};

export default WorkSchedulesSection;
